import { useMemo, useState } from 'react';

import {
  AssetDTO,
  FormDTO,
  FormType,
  IssueDTO,
  IssueSelectFormElementAnswerSchema,
  IssueSelectFormElement as IssueSelectFormElementType,
  IssueSource,
  IssueStatus,
  RecordState,
} from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { match } from 'ts-pattern';
import { z } from 'zod';

import EyeballIcon from '@/components/icons/eyeball';
import { PlusIcon } from '@/components/icons/plus';
import IssueDrawer from '@/components/issues/issue-drawer';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { getAssetIssuesQueryOptions, issueKeys } from '@/queries/issues';
import { useIsAWA } from '@/utils/awa';

import { IssueSelectConfigLabels } from '../config-field-labels';
import { ConfigLabel } from '../config-label';
import { FormFieldError } from '../form-field-error';

export const IssuesSelectFormElement = ({
  element,
  value,
  onChange,
  onBlur,
  readonly,
  asset,
  form,
  error,
  checkId,
  locationId,
  fieldReference,
}: {
  element: IssueSelectFormElementType;
  value?: z.infer<typeof IssueSelectFormElementAnswerSchema>['value'];
  onChange: (value: z.infer<typeof IssueSelectFormElementAnswerSchema>['value']) => void;
  onBlur?: () => void;
  readonly?: boolean;
  asset: AssetDTO;
  form: FormDTO;
  error?: string;
  checkId?: string;
  fieldReference?: string;
  locationId?: string;
}) => {
  const queryClient = useQueryClient();
  const { isAWA } = useIsAWA();

  const [issueDrawerOpen, setIssueDrawerOpen] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState<IssueDTO | undefined>(undefined);

  // Readonly views filter to Active state only — intentional per GB-1580 exploration (2026-03-04).
  // Archived/deleted issues are hidden even in historical inspections, consistent with the Issues Register.
  // If historical fidelity is needed in future, replace with issueId: value ?? [] to fetch the exact saved issues.
  const issueFilter = !readonly
    ? { issueStatus: [IssueStatus.Open], issueState: [RecordState.Active] as (RecordState.Active | RecordState.Archived)[] }
    : { issueState: [RecordState.Active] as (RecordState.Active | RecordState.Archived)[] };
  const { data: issuesData } = useQuery({
    ...getAssetIssuesQueryOptions({ assetId: asset.id, isAWA, filters: issueFilter }),
    enabled: !!asset.id && asset.id !== 'mock-asset-id',
  });

  const issues = useMemo(() => issuesData ?? [], [issuesData]);

  const handleIssueToggle = (issueId: string) => {
    const newValue = value?.includes(issueId) ? (value as string[]).filter((id) => id !== issueId) : [...(value ?? []), issueId];
    onChange(newValue);
    onBlur?.();
  };

  const handleAddIssueComplete = async (issue: IssueDTO) => {
    setIssueDrawerOpen(false);
    await queryClient.invalidateQueries({ queryKey: issueKeys.all });
    handleIssueToggle(issue.id);
  };

  return (
    <div className="space-y-1">
      <div className="space-y-2">
        <Label required={element.required}>{element.label}</Label>

        <div className="space-y-2">
          {issues.length === 0 && <p className="text-grey-5 text-sm">No open issues for this asset</p>}

          {issues
            .filter((issue) => !checkId || issue.checkFormElementId === checkId)
            .filter((issue) => (readonly ? true : issue.status === IssueStatus.Open))
            .filter((issue) => (!readonly ? true : value?.includes(issue.id)))
            .map((issue) => (
              <div key={issue.id} className="flex items-start gap-2 p-2">
                <Checkbox
                  id={`issue-${issue.id}-${element.id}`}
                  checked={value?.includes(issue.id) ?? false}
                  onCheckedChange={() => handleIssueToggle(issue.id)}
                  onBlur={onBlur}
                  disabled={readonly}
                  className="disabled:bg-grey-1 disabled:opacity-100"
                  size="2xl"
                />
                <div
                  className="flex-1 cursor-pointer text-sm"
                  onClick={() => {
                    setSelectedIssue(issue);
                    setIssueDrawerOpen(true);
                  }}>
                  <div className="flex justify-between">
                    <span className="font-medium">{issue.title ? issue.title : issue.fieldReference}</span>
                    <div className="flex items-center gap-2">
                      {issue.status === IssueStatus.Open && <EyeballIcon className="text-default-orange" />}
                      <span className="bg-default-red grid size-5 place-items-center rounded-full text-xs text-white">{issue.count}</span>
                    </div>
                  </div>
                  {issue.description && (
                    <p className="text-grey-6 mt-1 max-w-[250px] truncate sm:max-w-[300px] md:max-w-[400px] lg:max-w-[500px]">{issue.description}</p>
                  )}
                </div>
              </div>
            ))}

          {!readonly && (
            <Button type="button" variant="outline" size="sm" onClick={() => setIssueDrawerOpen(true)} className="gap-2">
              <PlusIcon className="size-4" />
              New issue
            </Button>
          )}
        </div>

        <FormFieldError error={error} />
      </div>

      <IssueDrawer
        noTrigger
        issueId={selectedIssue?.id}
        open={issueDrawerOpen}
        onOpenChange={setIssueDrawerOpen}
        asset={asset}
        callback={handleAddIssueComplete}
        requirePhotos={element.config.requirePhotos}
        source={match(form.type)
          .with(FormType.InspectionForm, () => IssueSource.Inspection)
          .with(FormType.ScheduleForm, () => IssueSource.Service)
          .otherwise(() => IssueSource.Manual)}
        checkFormElementId={checkId}
        locationId={locationId}
        fieldReference={fieldReference}
        largeButtons
      />
    </div>
  );
};

export function IssuesSelectFormElementMock({ element }: { element: IssueSelectFormElementType }) {
  const queryClient = useQueryClient();
  const { isAWA } = useIsAWA();
  const [value, setValue] = useState<string[]>([]);
  const [addIssueDrawerOpen, setAddIssueDrawerOpen] = useState(false);

  const { data: issuesData } = useQuery({
    ...getAssetIssuesQueryOptions({ assetId: 'mock-asset-id', isAWA }),
    enabled: false,
  });

  const issues = useMemo(() => issuesData ?? [], [issuesData]);

  const handleIssueToggle = (issueId: string) => {
    const newValue = value.includes(issueId) ? value.filter((id) => id !== issueId) : [...value, issueId];
    setValue(newValue);
  };

  const handleAddIssueComplete = async () => {
    setAddIssueDrawerOpen(false);
    queryClient.invalidateQueries({ queryKey: issueKeys.all });
  };

  return (
    <div className="space-y-2">
      <div className="space-y-2">
        {issues.length === 0 && <p className="text-grey-5 text-sm">No open issues for this asset</p>}

        {issues.map((issue) => (
          <div key={issue.id} className="border-grey-3 flex items-start gap-2 rounded-md border p-2">
            <Checkbox id={`issue-${issue.id}`} checked={value.includes(issue.id)} onCheckedChange={() => handleIssueToggle(issue.id)} />
            <label htmlFor={`issue-${issue.id}`} className="flex-1 cursor-pointer text-sm">
              <div className="flex justify-between">
                <span className="font-medium">{issue.fieldReference}</span>
                <div className="flex items-center gap-2">
                  {issue.status === IssueStatus.Open && <EyeballIcon className="text-default-orange" />}
                  <span className="bg-default-red grid size-5 place-items-center rounded-full text-xs text-white">{issue.count}</span>
                </div>
              </div>
              {issue.description && (
                <p className="text-grey-6 mt-1 max-w-[250px] truncate sm:max-w-[300px] md:max-w-[400px] lg:max-w-[500px]">{issue.description}</p>
              )}
            </label>
          </div>
        ))}

        <Button type="button" variant="outline" size="sm" onClick={() => setAddIssueDrawerOpen(true)} className="gap-2">
          <PlusIcon className="size-4" />
          New issue
        </Button>
      </div>

      <IssueDrawer
        noTrigger
        open={addIssueDrawerOpen}
        onOpenChange={setAddIssueDrawerOpen}
        callback={handleAddIssueComplete}
        requirePhotos={element.config.requirePhotos}
        source={IssueSource.Manual}
      />
    </div>
  );
}

export function IssuesSelectFormElementConfig({
  element,
  onChange,
}: {
  element: IssueSelectFormElementType;
  onChange?: (updates: Partial<IssueSelectFormElementType>) => void;
}) {
  return (
    <div className="flex flex-col gap-4">
      <ConfigLabel>
        <span>Label:</span>
        <Input value={element.label} onChange={(e) => onChange?.({ label: e.target.value })} />
      </ConfigLabel>

      <ConfigLabel className="flex-row items-start gap-2">
        <Checkbox checked={element.required} onCheckedChange={(checked) => onChange?.({ required: !!checked })} />
        <span>Required</span>
      </ConfigLabel>

      <ConfigLabel className="flex-row items-start gap-2">
        <Checkbox
          checked={element.config.requirePhotos}
          onCheckedChange={(checked) => onChange?.({ config: { ...element.config, requirePhotos: !!checked } })}
        />
        <span>{IssueSelectConfigLabels.requirePhotos}</span>
      </ConfigLabel>
    </div>
  );
}
